import React, { useState, useEffect } from 'react';
import { logger } from '../utils/logger';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Instagram, Mail } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [content, setContent] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/footer/content`);
        setContent(response.data);
      } catch (error) {
        logger.error('Failed to fetch footer content:', error);
      }
    };
    fetchContent();
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setSubscribing(true);
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success(response.data.message);
      setEmail('');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Er ging iets mis. Probeer het opnieuw.';
      toast.error(errorMsg);
    } finally {
      setSubscribing(false);
    }
  };

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const section = document.getElementById(sectionId);
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <footer className="bg-earth-brown text-white">
      {/* Newsletter Section - Separate Background */}
      <div className="bg-terra-beige/20 py-12 border-b border-earth-brown/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <Mail className="w-8 h-8 mx-auto mb-3 text-terra-beige" />
            <h3 className="font-sweet-sans text-2xl font-normal uppercase tracking-wide mb-2">
              {content?.newsletter_title || "Blijf op de hoogte"}
            </h3>
            <p className="font-sweet-sans text-sm text-white/80 mb-4">
              {content?.newsletter_description || "Ontvang als eerste nieuws over nieuwe collecties en exclusieve aanbiedingen."}
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex gap-2 max-w-md mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={content?.newsletter_placeholder || "Jouw email adres"}
                required
                disabled={subscribing}
                className="flex-1 px-4 py-2 bg-white text-earth-brown font-sweet placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-terra-beige"
              />
              <button
                type="submit"
                disabled={subscribing}
                className="px-6 py-2 bg-terra-beige hover:bg-terra-beige/90 text-white font-sweet transition-colors disabled:opacity-50"
              >
                {subscribing ? 'Bezig...' : (content?.newsletter_button || 'Inschrijven')}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Footer Content */}
      <div className="py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <h3 className="font-sweet-sans text-2xl font-normal uppercase tracking-wide mb-4">
              {content?.brand_name || "L'ART"}
            </h3>
            <p className="font-sweet-sans text-sm text-white/80 leading-relaxed">
              {content?.brand_description || "Handgemaakte manden uit België. Ambacht met aandacht voor detail en duurzaamheid."}
            </p>
          </div>
          
          <div>
            <h4 className="font-sweet-sans text-lg font-normal uppercase tracking-wide mb-4">Navigatie</h4>
            <ul className="font-sweet-sans text-sm space-y-2">
              <li><Link to="/shop" className="text-white/80 cursor-pointer">Collectie</Link></li>
              <li><a href="/#kleuren" onClick={(e) => { e.preventDefault(); scrollToSection('kleuren'); }} className="text-white/80 cursor-pointer">Kleuren</a></li>
              <li><a href="/#professionals" onClick={(e) => { e.preventDefault(); scrollToSection('professionals'); }} className="text-white/80 cursor-pointer">Professionals</a></li>
              <li><a href="/#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="text-white/80 cursor-pointer">Contact</a></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-sweet-sans text-lg font-normal uppercase tracking-wide mb-4">Informatie</h4>
            <ul className="font-sweet-sans text-sm space-y-2">
              <li><Link to="/shop" className="text-white/80 cursor-pointer">Webshop</Link></li>
              <li><a href="/#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="text-white/80 cursor-pointer">Contact</a></li>
              <li><Link to="/terms" className="text-white/80 cursor-pointer">Algemene voorwaarden</Link></li>
              <li><Link to="/privacy" className="text-white/80 cursor-pointer">Privacybeleid</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-sweet-sans text-lg font-normal uppercase tracking-wide mb-4">Contact</h4>
            <ul className="font-sweet-sans text-sm space-y-2 text-white/80">
              <li>{content?.company_name || "L'ART België"}</li>
              <li>{content?.address_line1 || "Prins Leopoldstraat 38"}</li>
              <li>{content?.address_line2 || "9700 Oudenaarde"}</li>
              <li className="pt-2">{content?.email || "info@l-art.be"}</li>
              <li>{content?.phone || "+32 471 98 34 04"}</li>
              <li className="pt-2 text-xs">BTW: {content?.btw_number || "BE0123.456.789"}</li>
              <li className="pt-3">
                <a 
                  href={content?.instagram_url || "https://instagram.com/l.art.be"} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-white hover:text-terra-beige transition-colors"
                >
                  <Instagram size={18} />
                  <span>{content?.instagram_handle || "@l.art.be"}</span>
                </a>
              </li>
            </ul>
          </div>
          </div>
          
          <div className="border-t border-white/20 pt-8 mt-8">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm font-sweet-sans text-white/70">
              <p>{content?.copyright_text || "© 2025 L'ART. Alle rechten voorbehouden."}</p>
              <div className="flex gap-4">
                <Link to="/privacy" className="hover:text-white transition-colors">Privacybeleid</Link>
                <Link to="/terms" className="hover:text-white transition-colors">Algemene Voorwaarden</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};